// 🔤 **글자 자리에 글자 앉히기**(순수 계산) — 회장님 2026-09-14
// ─────────────────────────────────────────────────────────────────────────────
// 자리(`gen-text-slots`)가 «어디»라면 여기는 «그 안에 어떻게 앉나»다: 줄바꿈과 `MbTextOverlay` 조립.
//
// ★★왜 줄바꿈을 **미리** 계산해 `\n` 으로 박아 넣는가★★ — 이것이 이 파일의 존재 이유다.
//   이 글자를 그리는 렌더러는 둘이다: 캔버스(`mediabunnyRender.drawTextOverlays`)와
//   remotion(`TextOverlayLabelNode`). 그런데 **캔버스 쪽에는 자동 줄바꿈이 없다** — `\n` 분할만 한다
//   (그쪽 `fitToFrame` 주석이 그 사실과, 한쪽만 접으면 «두 렌더러의 그림이 갈린다»는 결론을 이미
//   적어 두었다). ⇒ 렌더러에 줄바꿈을 맡기면 미리보기와 결과물이 달라진다. 그래서 **줄을 데이터에
//   확정해서** 두 렌더러에 같은 문자열을 준다. 정합이 «검사로 지켜지는 것»이 아니라 **구조적으로
//   성립**하게 만드는 쪽을 골랐다.
//
// ★폭 계산은 추정이다(그리고 추정이어도 된다)★ — 실제 글자 폭은 글꼴이 정하고 우리는 카탈로그의
//   여러 글꼴을 허용한다. 하지만 위 이유로 **두 렌더러가 같은 줄을 그린다는 사실은 추정과 무관하게
//   보장**된다. 추정이 빗나가면 «한 줄이 조금 짧거나 길다»일 뿐 «미리보기와 결과가 다르다»가 아니다.
import type { MbTextOverlay } from "../source-text-overlay";
import { slotOverlayId, slotTextAnchor, type GenTextSlot, type GenTextSlotRect } from "./gen-text-slots";
import { normalizeSlotRuns, runsFromStyledChars, styledCharsOf, type SlotTextRun } from "./gen-text-run";
import type { MbTextRun } from "../text-runs";

/** 📐 프레임 픽셀 크기 — 자리는 정규화 좌표라 실제 폭을 알아야 몇 자가 들어가는지 계산된다. */
export interface GenFrameSize {
  width: number;
  height: number;
}

/**
 * 📐 **화면비 → 줄바꿈 계산용 프레임**.
 *
 * ★절대 크기는 결과에 영향을 주지 않는다★ — 한 줄에 들어가는 em 수는
 *   `(rect.w × W) / (sizePct × H)` = `(rect.w / sizePct) × (W / H)` 라, **가로세로 비만** 남는다.
 *   그래서 여기 숫자는 «그 비율의 대표값»이고, 컴포지션 해상도가 바뀌어도 줄이 달라지지 않는다
 *   (줄이 해상도에 따라 갈리면 미리보기와 합본이 어긋난다 — 이 축의 제1 불변식).
 */
export function genFrameForAspect(aspect: string | null | undefined): GenFrameSize {
  return String(aspect ?? "").trim() === "16:9" ? { width: 1920, height: 1080 } : { width: 1080, height: 1920 };
}

/**
 * 📏 **글자 한 자의 가로 폭(em 배수) 추정** — 한글은 정사각(전각)에 가깝고 라틴·숫자는 그 절반쯤이다.
 * ★한 곳에 모아 둔다★: 줄바꿈이 이 값 하나에 걸려 있어, 실측으로 조정할 때 고칠 자리가 하나여야 한다.
 */
export function charAdvanceEm(ch: string): number {
  if (ch === " ") return 0.32;
  const code = ch.codePointAt(0) ?? 0;
  // 한글(음절·자모)·CJK·전각 문장부호 = 전각
  const isWide =
    (code >= 0xac00 && code <= 0xd7a3) ||
    (code >= 0x1100 && code <= 0x11ff) ||
    (code >= 0x3130 && code <= 0x318f) ||
    (code >= 0x3000 && code <= 0x303f) ||
    (code >= 0x4e00 && code <= 0x9fff) ||
    (code >= 0xff01 && code <= 0xff60);
  if (isWide) return 1.0;
  if (/[.,:;!?'"()[\]]/.test(ch)) return 0.32;
  if (/[iIl1|]/.test(ch)) return 0.32;
  return 0.55;
}

/** 📏 문자열의 가로 폭(em 배수). */
export function textWidthEm(s: string): number {
  let w = 0;
  for (const ch of s) w += charAdvanceEm(ch);
  return w;
}

/** 🔤 줄바꿈 결과. */
export interface SlotWrapResult {
  /** 렌더에 그대로 나가는 문자열(`\n` 포함). ★유저가 쓴 글자는 한 자도 버리지 않는다★. */
  text: string;
  /** 줄 목록. */
  lines: string[];
  /**
   * 🔎 **실제로 쓰인 글자 크기** — 자리에 안 들어가면 declared 보다 작아진다(shrink-to-fit).
   * 호출부는 이 값을 오버레이에 실어야 한다(선언값을 실으면 렌더가 다시 넘친다).
   */
  sizePct: number;
  /** 줄인 적이 있는가 — 편집창이 «글자를 조금 줄였어요»를 말할 근거. */
  shrunk: boolean;
  /**
   * ⚠️ **가장 작게 줄여도 자리를 넘치는가**.
   * ★넘쳐도 글자는 안 지운다★ — 지우면 유저가 쓴 문장의 뒷부분이 영상에서 사라진 걸 알 방법이 없다.
   *   대신 사실대로 알리고(편집창이 경고) 화면에는 전부 그린다.
   */
  overflow: boolean;
  /** 넘칠 때 자리 밖으로 삐져나가는 줄들(지워진 것이 **아니다** — 안내용). */
  overflowText: string;
  /**
   * 🎨 **렌더로 나가는 글자 토막** — `runs.map(r => r.text).join("") === text` 가 불변식이다.
   * 꾸민 글자가 하나도 없으면 «색도 크기도 기본인 토막 한 장»이라, 호출부가 그때는 아예 안 싣는다.
   */
  runs: MbTextRun[];
  /** 🎨 꾸민 글자가 하나라도 있는가 — 없으면 종전 저장분과 **완전히 같은 모양**으로 저장한다. */
  styled: boolean;
  /** 📏 이 글에서 가장 큰 크기 배수(없으면 1) — 줄 높이와 «몇 줄 들어가나»의 근거. */
  maxScale: number;
}

/** 📉 shrink-to-fit 하한 — 선언 크기의 이 비율보다 작게는 안 줄인다(읽을 수 없어지면 줄인 의미가 없다). */
export const SLOT_MIN_SHRINK_RATIO = 0.6;

/** 🎨 꾸밈을 달고 다니는 글자 하나 — 줄 나누기가 문자열이 아니라 이 배열 위에서 돈다. */
type StyledChar = { ch: string; color?: string; scale?: number };

/**
 * 📏 글자 목록의 가로 폭(em) — ★크기 배수를 곱한다★.
 * 배수를 안 곱하면 «크게 만든 낱말»이 상자 밖으로 새고, 그때 줄 수 판정도 함께 틀린다.
 */
function widthEmOf(chars: readonly StyledChar[]): number {
  let w = 0;
  for (const c of chars) w += charAdvanceEm(c.ch) * (c.scale ?? 1);
  return w;
}

/**
 * 🔤 한 크기에서의 줄 나누기(내부) — 어절 경계 우선, 한 어절이 한 줄보다 길면 글자 단위로 쪼갠다.
 *
 * ★꾸밈이 하나도 없으면 결과가 종전과 **글자까지 같다**★ — 배수가 전부 1이면 `widthEmOf` 는
 *   `textWidthEm` 과 같은 수를 내고 어절 분해·이어붙이기도 같은 규칙이라, 기존 저장분의 줄이
 *   이 개편으로 1픽셀도 움직이지 않는다(스위트가 값으로 잠근다).
 */
function wrapStyled(chars: readonly StyledChar[], maxEm: number): StyledChar[][] {
  const lines: StyledChar[][] = [];
  const paragraphs: StyledChar[][] = [[]];
  for (const c of chars) {
    if (c.ch === "\n") paragraphs.push([]);
    else paragraphs[paragraphs.length - 1].push(c);
  }
  for (const paragraph of paragraphs) {
    // 어절 나누기 — 종전 `.trim().split(/\s+/)` 과 같은 처분(공백은 버리고 하나로 다시 넣는다).
    const words: StyledChar[][] = [];
    let word: StyledChar[] = [];
    for (const c of paragraph) {
      if (/\s/.test(c.ch)) { if (word.length) { words.push(word); word = []; } }
      else word.push(c);
    }
    if (word.length) words.push(word);
    if (!words.length) continue;

    let cur: StyledChar[] = [];
    const push = () => { if (cur.length) { lines.push(cur); cur = []; } };
    for (const w of words) {
      // 이어 붙이는 공백은 **앞 글자의 꾸밈**을 따른다 — 눈에 안 보이지만, 그래야 토막이 안 쪼개진다.
      const sep: StyledChar[] = cur.length ? [{ ...cur[cur.length - 1], ch: " " }] : [];
      const candidate = [...cur, ...sep, ...w];
      if (widthEmOf(candidate) <= maxEm) { cur = candidate; continue; }
      push();
      if (widthEmOf(w) <= maxEm) { cur = w; continue; }
      let chunk: StyledChar[] = [];
      for (const c of w) {
        if (widthEmOf([...chunk, c]) > maxEm && chunk.length) { lines.push(chunk); chunk = [c]; }
        else chunk.push(c);
      }
      cur = chunk;
    }
    push();
  }
  return lines;
}

/**
 * 🔤 **자리에 글을 앉힌다** — 폭에 맞춰 줄을 나누고, 줄 수가 넘치면 **글자를 줄여서** 맞춘다.
 *
 * ★설계 원칙: 유저가 쓴 글자는 한 자도 버리지 않는다★. 종전 판은 `maxLines` 를 넘는 줄을 **잘랐는데**,
 *   그러면 저장 → 다시 읽기에서 «잘린 결과»가 원문 행세를 해 **넘쳤다는 사실 자체가 사라졌다**
 *   (조용한 데이터 손실 · 2026-09-14 스위트가 잡았다). ⇒ 이제 세 단계로 처분한다:
 *     ① 선언 크기로 들어가면 그대로 ② 안 들어가면 하한(`SLOT_MIN_SHRINK_RATIO`)까지 **줄여서** 맞춤
 *     ③ 하한에서도 넘치면 **전부 그리되 `overflow` 로 알린다**(편집창이 경고 · 글자는 그대로).
 *
 * 유저가 넣은 `\n` 은 문단 경계로 존중한다.
 *
 * @param runs 유저가 «이 구간만» 바꿔 둔 색·크기(원문 기준 구간 · 회장님 2026-09-15).
 *   ★크기 배수는 줄 계산을 **둘 다** 바꾼다★ — 폭(한 줄에 몇 자)과 줄 수(상자에 몇 줄)가 같이 준다.
 *   한쪽만 반영하면 «큰 글씨를 넣었더니 상자 밖으로 샌다»가 된다.
 */
export function wrapTextForSlot(
  rawText: string,
  slot: Pick<GenTextSlot, "rect" | "look">,
  frame: GenFrameSize,
  runs?: readonly SlotTextRun[],
): SlotWrapResult {
  const text = String(rawText ?? "").trim();
  const { rect, look } = slot;
  const declared = look.sizePct;
  const boxPx = Math.max(1, rect.w * frame.width);
  const empty = (): SlotWrapResult => ({
    text: "", lines: [], sizePct: declared, shrunk: false, overflow: false, overflowText: "",
    runs: [], styled: false, maxScale: 1,
  });
  if (!text) return empty();

  const chars = styledCharsOf(text, runs);
  const styled = chars.some((c) => c.color !== undefined || c.scale !== undefined);
  let maxScale = 1;
  for (const c of chars) maxScale = Math.max(maxScale, c.scale ?? 1);
  // 📏 큰 글씨가 섞이면 줄도 그만큼 높아져 **들어가는 줄 수가 준다**(줄 높이는 줄 안 최대 배수를 탄다).
  const maxLines = Math.max(1, Math.floor(Math.max(1, look.maxLines) / maxScale));

  const linesAt = (sizePct: number) => wrapStyled(chars, boxPx / Math.max(1, sizePct * frame.height));

  /* ✂️🚫 **낱말(또는 직접 나눈 줄)을 중간에서 끊지 않는다**(2026-10-01 · 운영 칠판 «종신보험으 / 로») — 한 덩이가 자리 폭보다 넓으면
     먼저 글자를 줄여 그 덩이가 한 줄에 들게 한다(하한 `SLOT_MIN_SHRINK_RATIO`). 덩이 = 원문에 줄바꿈이 있으면 그 줄, 없으면 낱말.
     하한에서도 안 들면 종전대로 글자 단위로 나눈다. 덩이가 다 들어가는 글은 종전과 글자까지 같다. */
  const units: StyledChar[][] = [];
  {
    const explicit = chars.some((c) => c.ch === "\n");
    let cur: StyledChar[] = [];
    const flush = () => { if (cur.length) units.push(cur); cur = []; };
    for (const c of chars) {
      if (c.ch === "\n" || (!explicit && /\s/.test(c.ch))) { flush(); continue; }
      if (explicit && !cur.length && /\s/.test(c.ch)) continue;
      cur.push(c);
    }
    flush();
  }
  const widestEm = units.reduce((m, u) => Math.max(m, widthEmOf(u)), 0);
  const fitSize = widestEm > 0 ? boxPx / (widestEm * frame.height) : declared;
  let sizePct = fitSize < declared ? Math.max(declared * SLOT_MIN_SHRINK_RATIO, fitSize * 0.999) : declared;
  let lines = linesAt(sizePct);
  // 📉 2% 씩 줄여 가며 맞춘다(연속 탐색은 불필요 — 줄 수는 계단 함수라 촘촘한 스텝이면 충분하다).
  if (lines.length > maxLines) {
    const floor = declared * SLOT_MIN_SHRINK_RATIO;
    for (let next = sizePct * 0.98; next >= floor; next *= 0.98) {
      const cand = linesAt(next);
      sizePct = next;
      lines = cand;
      if (cand.length <= maxLines) break;
    }
  }

  // 🎨 줄 사이 `\n` 도 **앞 글자의 꾸밈**을 달아 둔다 — 토막이 줄마다 쪼개지지 않게(렌더는 어차피 나눈다).
  const flat: StyledChar[] = [];
  lines.forEach((ln, i) => {
    if (i) flat.push({ ...(flat[flat.length - 1] ?? {}), ch: "\n" });
    flat.push(...ln);
  });

  const overflow = lines.length > maxLines;
  return {
    text: lines.map((ln) => ln.map((c) => c.ch).join("")).join("\n"),
    lines: lines.map((ln) => ln.map((c) => c.ch).join("")),
    sizePct,
    shrunk: sizePct < declared,
    overflow,
    overflowText: overflow ? lines.slice(maxLines).map((ln) => ln.map((c) => c.ch).join("")).join(" ") : "",
    runs: runsFromStyledChars(flat),
    styled,
    maxScale,
  };
}

/** 🔤 자리에 앉힌 오버레이 + 처분 정보. */
export interface SlotOverlayBuild {
  overlay: MbTextOverlay;
  /** 하한까지 줄여도 자리를 넘치는가(글자는 안 지웠다). */
  overflow: boolean;
  /** 자리 밖으로 삐져나가는 부분(안내용 — 지워진 것이 아니다). */
  overflowText: string;
  /** 자리에 맞추려고 글자를 줄였는가. */
  shrunk: boolean;
}

/**
 * 🔤 **자리 + 글 → `MbTextOverlay` 한 장**.
 *
 * ★새 표시 어휘를 만들지 않는다★ — 나오는 것은 두 렌더러가 이미 읽는 기존 타입 그대로다.
 *   그래서 이 층은 «렌더러를 고치는 일» 없이 성립한다.
 * ★`id` 로 신원을 남긴다★ — 편집 창구가 «자리가 깐 것»을 알아보고, 다시 채울 때 남의 오버레이를
 *   건드리지 않고 자기 것만 갈아끼울 수 있다.
 * ★실린 `sizePct` 는 **실제로 쓰인 크기**다★(선언값이 아니다) — shrink-to-fit 결과를 안 실으면
 *   렌더가 원래 크기로 그려 자리를 다시 넘친다.
 *
 * @param overrides 유저가 룩을 덮어쓴 값(크기·색·정렬 등). 없으면 스타일 기본값.
 */
export interface SlotBuildOverrides {
  /** 유저가 바꾼 룩. */
  look?: Partial<Pick<GenTextSlot["look"], "sizePct" | "color" | "outline" | "weight" | "align">>;
  /** 🔤 유저가 고른 서체(카탈로그 family). 미지정 = 렌더 전역 제목 폰트. */
  fontFamily?: string;
  /**
   * 📐 유저가 미리보기 위에서 끌어 만든 상자(정규화 · 좌상단 기준). 미지정 = 스타일 선언 자리.
   * ★상자를 주면 줄바꿈 폭·줄 수·기준점이 **전부 이 상자에서** 나온다★ — 일부만 따르면
   *   «폭은 유저 상자, 줄 수는 스타일» 같은 반쪽 상태가 생겨 글자가 상자 밖으로 샌다.
   */
  rect?: GenTextSlotRect;
  /**
   * 🎨 **유저가 «이 구간만» 바꿔 둔 색·크기**(원문 기준 구간 · 회장님 2026-09-15).
   * 미지정 = 상자 전체가 한 가지 색·크기(기존 저장분과 완전히 같은 모양).
   */
  runs?: readonly SlotTextRun[];
}

/**
 * 📐 유저 상자가 있으면 그 상자를, 없으면 스타일 선언 자리를 쓴다.
 * 줄 수는 상자 높이에서 파생한다 — 상자를 키우면 더 많은 줄이 들어가야 상식과 맞는다.
 */
function effectiveSlot(slot: GenTextSlot, ov?: SlotBuildOverrides): { rect: GenTextSlotRect; look: GenTextSlot["look"] } {
  const look = { ...slot.look, ...(ov?.look ?? {}) };
  const rect = ov?.rect ?? slot.rect;
  if (!ov?.rect) return { rect, look };
  // 선언 자리에서 «높이 대비 몇 줄» 비율을 가져와 상자 높이에 다시 태운다.
  const perLine = slot.rect.h / Math.max(1, slot.look.maxLines);
  const maxLines = Math.max(1, Math.round(rect.h / Math.max(1e-6, perLine)));
  return { rect, look: { ...look, maxLines } };
}

export function buildSlotOverlay(
  slot: GenTextSlot,
  rawText: string,
  frame: GenFrameSize,
  overrides?: SlotBuildOverrides,
): SlotOverlayBuild {
  const { rect, look } = effectiveSlot(slot, overrides);
  const source = String(rawText ?? "").trim();
  // 🎨 구간은 **원문 길이 안**으로 잘라서 들고 간다(문구가 짧아졌는데 구간만 남는 일이 없게).
  // 🟨 끝 줄 강조(선언한 자리만 · 2026-10-01) — 원문이 두 줄 이상이고 유저가 칠한 구간이 없을 때만 기본으로 깐다.
  const userRuns = overrides?.runs?.length ? overrides.runs : undefined;
  const lastNl = source.lastIndexOf("\n");
  const accentRuns: SlotTextRun[] | undefined = !userRuns && look.accentLastLine && lastNl >= 0 && lastNl + 1 < source.length
    ? [{ start: lastNl + 1, end: source.length, color: look.accentLastLine }]
    : undefined;
  const slotRuns = normalizeSlotRuns(userRuns ?? accentRuns, source.length);
  const wrapped = wrapTextForSlot(rawText, { rect, look }, frame, slotRuns);
  const anchor = slotTextAnchor({ rect, look });
  const overlay: MbTextOverlay = {
    id: slotOverlayId(slot.slotKey),
    text: wrapped.text,
    // 🔤 원문 동승 — `text` 의 줄바꿈은 되돌릴 수 없다(띄어쓰기였는지 강제 분할이었는지 모른다).
    slotText: source,
    x: anchor.x,
    y: anchor.y,
    sizePct: wrapped.sizePct,
    color: look.color,
    weight: look.weight,
    align: look.align,
    outline: look.outline,
    // 🌫 그림자 — 선언한 자리만 싣는다(미선언 = 키 없음 = 종전 저장분과 바이트 동일).
    ...(look.shadow ? { shadow: true } : {}),
    // 📐 상자를 **되쓸 수 있게** 실어 둔다 — 다음 편집이 같은 폭으로 다시 감싸야 글자가 안 흔들린다.
    ...(overrides?.rect ? { boxW: rect.w, boxH: rect.h } : {}),
    ...(overrides?.fontFamily ? { fontFamily: overrides.fontFamily } : {}),
    // 📏 유저가 고른 **기준** 크기 — `sizePct` 는 shrink 결과라 되읽으면 «줄였다»가 사라진다.
    ...(overrides?.look?.sizePct ? { slotSizePct: look.sizePct } : {}),
    // 🎨 **꾸민 글자가 있을 때만** 두 축을 싣는다 — 없으면 종전 저장분과 바이트까지 같은 모양이다
    //    (`runs` 가 «전체 한 토막»으로라도 깔리면 두 렌더러가 새 경로를 타 픽셀이 미세하게 움직인다).
    ...(wrapped.styled ? { runs: wrapped.runs, slotRuns } : {}),
    // 🟨 끝 줄 강조가 깐 구간이면 표식을 단다 — 다음 빌드가 유저 구간으로 오인하지 않게(`overridesOf`).
    ...(wrapped.styled && accentRuns ? { slotAccent: true } : {}),
    // 🕐 **등장 시각** — 선언한 자리만 싣는다. 미선언이면 키 자체가 없어 종전 저장분과 바이트까지 같다
    //    (렌더는 «표시 시작»이 없으면 컷 내내 그린다). 끝 시각은 두지 않는다 — 한 번 뜬 글자는 그 컷이
    //    끝날 때까지 남는 것이 이 축의 계약이고, 사라지는 글자가 필요하면 유저가 직접 잡는다.
    ...(typeof slot.appearSec === "number" && slot.appearSec > 0 ? { start: slot.appearSec } : {}),
  };
  return { overlay, overflow: wrapped.overflow, overflowText: wrapped.overflowText, shrunk: wrapped.shrunk };
}

/**
 * 🧮 **이 자리에 들어가는 대략 글자 수** — 편집 창구의 «몇 자까지» 안내용.
 * 한글 기준(전각 1.0em)이라 라틴이 섞이면 실제로는 더 들어간다(보수적 안내).
 */
export function slotCapacityKoreanChars(
  slot: Pick<GenTextSlot, "rect" | "look">,
  frame: GenFrameSize,
): number {
  const fontPx = Math.max(1, slot.look.sizePct * frame.height);
  const boxPx = Math.max(1, slot.rect.w * frame.width);
  return Math.max(1, Math.floor(boxPx / fontPx) * Math.max(1, slot.look.maxLines));
}

/**
 * 📐 **자리 글자가 자막 블록 윗변을 넘지 않게**(렌더 직전 · 순수 · 2026-10-01 회장님 «자막 폰트가 칠판 폰트를 가려 지저분»).
 *
 * ★왜 렌더 직전인가★ — 자막 블록 윗변은 작업물마다 다르다(자막 크기·한 화면 줄 수를 유저가 바꾼다). 저장 시점에 접으면
 *   자막을 키운 뒤 다시 겹친다. 그래서 그리는 순간의 윗변으로 맞춘다(저장 데이터는 손대지 않는다).
 * ★규칙★ — 글 블록을 [자리 윗변, min(자리 바닥, 자막 윗변 − 틈)] 안에 담는다: 들어가면 **그대로**(같은 객체),
 *   높으면 글자를 줄이고(하한 없음 — 겹치는 것보다 작은 것이 낫다), 위치는 그 구간 안으로 옮긴다.
 * ★줄 높이는 넉넉히 잡는다★(1.3em × 구간 최대 배수) — 렌더러의 `line-height: normal` 은 서체마다 1.1~1.3 이라 작게 잡으면 겹친다.
 * @param rect 자리 사각형(정규화 · 좌상단 기준)
 * @param captionTopNorm 자막 블록 윗변(정규화 0..1). `null` = 자막을 안 그린다 → 그대로.
 */
export function fitSlotOverlayAboveCaption<T extends Pick<MbTextOverlay, "text" | "y" | "sizePct" | "runs">>(
  o: T,
  rect: GenTextSlotRect,
  captionTopNorm: number | null | undefined,
  opts?: { gapNorm?: number; lineHeightEm?: number },
): T {
  if (typeof captionTopNorm !== "number" || !Number.isFinite(captionTopNorm)) return o;
  const text = String(o?.text ?? "").trim();
  const size = Number(o?.sizePct);
  const cy = Number(o?.y);
  if (!text || !(size > 0) || !Number.isFinite(cy)) return o;
  const gap = opts?.gapNorm ?? 0.012;
  const top = rect.y;
  const bottom = Math.min(rect.y + rect.h, captionTopNorm - gap);
  const room = bottom - top;
  if (!(room > 0)) return o;
  const runScale = Array.isArray(o.runs) && o.runs.length ? Math.max(1, ...o.runs.map((r) => Number((r as { scale?: unknown })?.scale) || 1)) : 1;
  const lines = text.split("\n").length;
  const h = lines * size * (opts?.lineHeightEm ?? 1.3) * runScale;
  if (cy - h / 2 >= top - 1e-9 && cy + h / 2 <= bottom + 1e-9) return o;
  const k = Math.min(1, room / h);
  const h2 = h * k;
  const y = Math.min(Math.max(cy, top + h2 / 2), bottom - h2 / 2);
  return { ...o, sizePct: size * k, y };
}
